/**
 * The login wall + paywall stub shown by the entry gate (sc-6940,
 * email-OTP sc-7039).
 *
 * Login wall: sells the 5 free daily tokens (not just "sign in"), offers
 * [Continue with Google] (primary, redirect) and an email path that is
 * now EMAIL-OTP (sc-7039): enter email → 6-digit code arrives → enter it
 * → signed in IN PLACE (no redirect). Magic-link is demoted to a small
 * secondary "email me a link instead" fallback.
 *
 * Google leaves the page (redirect → /auth/callback), so we stash the
 * pending cartridge for relaunch. Email-OTP verifies without leaving, so
 * it establishes the session directly and relaunches the pending
 * cartridge itself via onSignedIn().
 *
 * Paywall (grant:'paywall') is a STUB until sc-6944 builds the real
 * pricing surface — it states the situation and the reset time; passes
 * aren't purchasable yet.
 */

import { oauthStart } from '../api/client.js';
import { sendEmailOtp, sendMagicLink, verifyEmailOtp } from '@arcraz/arcadia-auth-web';
import { establishSession } from '../auth/establish.js';

const PENDING_LAUNCH_KEY = 'a84.pendingLaunch';
const OAUTH_STATE_KEY = 'a84.oauth.state';
const OAUTH_PROVIDER_KEY = 'a84.oauth.provider';

/** Remember the cartridge to auto-launch after the auth round-trip. */
export function setPendingLaunch(cartridgeId: string): void {
  try {
    sessionStorage.setItem(PENDING_LAUNCH_KEY, cartridgeId);
  } catch {
    /* private mode — auto-relaunch just won't happen; login still works */
  }
}

/** Read + clear the pending cartridge (called on boot after callback). */
export function consumePendingLaunch(): string | null {
  try {
    const id = sessionStorage.getItem(PENDING_LAUNCH_KEY);
    sessionStorage.removeItem(PENDING_LAUNCH_KEY);
    return id;
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------- overlay

let activeWall: HTMLElement | null = null;

function dismiss(): void {
  activeWall?.remove();
  activeWall = null;
}

/**
 * Show the login wall. Returns a promise that resolves when the player
 * dismisses it (back to gallery); the auth actions navigate away, so
 * those paths never resolve here.
 *
 * Two callers:
 *   - The entry gate (INSERT COIN, taste used) passes the cartridge the
 *     player was trying to coin, plus onSignedIn so an in-place email-OTP
 *     sign-in can relaunch it.
 *   - The footer "SIGN IN" button passes nothing — a bare sign-in.
 *
 * `onSignedIn` fires only for the IN-PLACE email-OTP path (the player is
 * signed in without leaving the page). Google/magic-link leave the page
 * and resume via /auth/callback instead, so they never call it.
 */
export function showLoginWall(
  cartridgeId?: string,
  cartridgeTitle?: string,
  onSignedIn?: () => void
): Promise<void> {
  return new Promise((resolve) => {
    dismiss();
    const refs = buildWall('login', cartridgeTitle ?? '');
    const {
      root, googleBtn, emailBtn, emailForm, emailInput, emailSend,
      codeForm, codeInput, codeVerify, magicLinkBtn, status, closeBtn,
    } = refs;
    activeWall = root;
    document.body.appendChild(root);

    const close = (): void => { dismiss(); resolve(); };
    closeBtn.addEventListener('click', close);
    root.addEventListener('click', (e) => { if (e.target === root) close(); });

    // Google: stash the pending cartridge (if the gate gave us one) +
    // provider, ask the API for the authorization URL, then redirect.
    // `state` is stashed for the callback's CSRF check.
    googleBtn.addEventListener('click', () => {
      void (async () => {
        googleBtn.disabled = true;
        status.textContent = 'Redirecting to Google…';
        try {
          if (cartridgeId) setPendingLaunch(cartridgeId);
          const { authorizationUrl, state } = await oauthStart(
            'google',
            `${window.location.origin}/auth/callback`
          );
          sessionStorage.setItem(OAUTH_STATE_KEY, state);
          sessionStorage.setItem(OAUTH_PROVIDER_KEY, 'google');
          window.location.assign(authorizationUrl);
        } catch (err) {
          googleBtn.disabled = false;
          status.textContent =
            err instanceof Error ? `Couldn't start Google sign-in: ${err.message}` : 'Sign-in failed.';
        }
      })();
    });

    // Email: reveal the email input (step 1 of the OTP flow).
    emailBtn.addEventListener('click', () => {
      emailForm.hidden = false;
      emailInput.focus();
    });

    // Remembered between the send + verify steps.
    let otpEmail = '';
    const emailOk = (e: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);

    // Step 1 — send the OTP code to the email.
    const sendCode = (): void => {
      const email = emailInput.value.trim();
      if (!emailOk(email)) {
        status.textContent = 'Enter a valid email address.';
        emailInput.focus();
        return;
      }
      status.textContent = 'Sending your code…';
      emailSend.disabled = true;
      void sendEmailOtp(email)
        .then(() => {
          otpEmail = email;
          // Move to step 2: hide email input, reveal the code input.
          emailForm.hidden = true;
          codeForm.hidden = false;
          codeInput.value = '';
          codeInput.focus();
          status.textContent = `We emailed a 6-digit code to ${email}.`;
        })
        .catch((err) => {
          emailSend.disabled = false;
          status.textContent =
            err instanceof Error ? `Couldn't send the code: ${err.message}` : 'Send failed.';
        });
    };
    emailSend.addEventListener('click', sendCode);
    emailInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') { e.preventDefault(); sendCode(); }
    });

    // Step 2 — verify the code → session established IN PLACE.
    const verifyCode = (): void => {
      const code = codeInput.value.trim();
      if (code.length < 4) {
        status.textContent = 'Enter the code from your email.';
        codeInput.focus();
        return;
      }
      status.textContent = 'Verifying…';
      codeVerify.disabled = true;
      void verifyEmailOtp({ email: otpEmail, code })
        .then((session) => {
          establishSession(session);
          status.textContent = 'Signed in!';
          dismiss();
          resolve();
          onSignedIn?.();
        })
        .catch((err) => {
          codeVerify.disabled = false;
          status.textContent =
            err instanceof Error ? `That didn't work: ${err.message}` : 'Verification failed.';
          codeInput.focus();
        });
    };
    codeVerify.addEventListener('click', verifyCode);
    codeInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') { e.preventDefault(); verifyCode(); }
    });

    // Secondary fallback — magic link instead of a code (leaves the page).
    magicLinkBtn.addEventListener('click', () => {
      const email = (emailInput.value || otpEmail).trim();
      if (!emailOk(email)) {
        emailForm.hidden = false;
        status.textContent = 'Enter your email first, then tap the link option.';
        emailInput.focus();
        return;
      }
      status.textContent = 'Sending your sign-in link…';
      magicLinkBtn.disabled = true;
      if (cartridgeId) setPendingLaunch(cartridgeId);
      void sendMagicLink({ email, redirectUrl: `${window.location.origin}/auth/callback` })
        .then(() => { status.textContent = 'Check your email for a sign-in link.'; })
        .catch((err) => {
          magicLinkBtn.disabled = false;
          status.textContent =
            err instanceof Error ? `Couldn't send the link: ${err.message}` : 'Send failed.';
        });
    });
  });
}

/** Paywall stub (grant:'paywall') — real pricing lands in sc-6944. */
export function showPaywallStub(resetsAtUtc?: string): Promise<void> {
  return new Promise((resolve) => {
    dismiss();
    const { root, closeBtn, status } = buildWall('paywall', '');
    activeWall = root;
    if (resetsAtUtc) {
      const reset = new Date(resetsAtUtc);
      const mins = Math.max(0, Math.round((reset.getTime() - Date.now()) / 60000));
      const h = Math.floor(mins / 60);
      status.textContent = h > 0
        ? `Free tokens refill in about ${h}h ${mins % 60}m.`
        : `Free tokens refill in about ${mins}m.`;
    }
    document.body.appendChild(root);
    const close = (): void => { dismiss(); resolve(); };
    closeBtn.addEventListener('click', close);
    root.addEventListener('click', (e) => { if (e.target === root) close(); });
  });
}

// ---------------------------------------------------------------- DOM

interface WallRefs {
  root: HTMLElement;
  googleBtn: HTMLButtonElement;
  emailBtn: HTMLButtonElement;
  emailForm: HTMLElement;
  emailInput: HTMLInputElement;
  emailSend: HTMLButtonElement;
  codeForm: HTMLElement;
  codeInput: HTMLInputElement;
  codeVerify: HTMLButtonElement;
  magicLinkBtn: HTMLButtonElement;
  status: HTMLElement;
  closeBtn: HTMLButtonElement;
}

function buildWall(kind: 'login' | 'paywall', title: string): WallRefs {
  const root = document.createElement('div');
  root.className = 'play-gate-wall';
  root.setAttribute('role', 'dialog');
  root.setAttribute('aria-modal', 'true');

  const card = document.createElement('div');
  card.className = 'play-gate-wall__card';

  const eyebrow = document.createElement('div');
  eyebrow.className = 'play-gate-wall__eyebrow';
  eyebrow.textContent = kind === 'login' ? 'INSERT COIN' : 'OUT OF TOKENS';

  const heading = document.createElement('h2');
  heading.className = 'play-gate-wall__title';
  heading.textContent = kind === 'login'
    ? 'Sign in to keep playing'
    : "You're out of tokens";

  const pitch = document.createElement('p');
  pitch.className = 'play-gate-wall__pitch';
  pitch.textContent = kind === 'login'
    ? 'Free account = 5 plays every day. No card, no catch.'
    : 'Come back tomorrow for 5 more — or grab a pass (coming soon).';

  card.append(eyebrow, heading, pitch);

  // login-only auth controls
  const googleBtn = document.createElement('button');
  const emailBtn = document.createElement('button');
  const emailForm = document.createElement('div');
  const emailInput = document.createElement('input');
  const emailSend = document.createElement('button');
  const codeForm = document.createElement('div');
  const codeInput = document.createElement('input');
  const codeVerify = document.createElement('button');
  const magicLinkBtn = document.createElement('button');
  if (kind === 'login') {
    googleBtn.type = 'button';
    googleBtn.className = 'play-gate-wall__google';
    googleBtn.textContent = 'Continue with Google';

    emailBtn.type = 'button';
    emailBtn.className = 'play-gate-wall__email-toggle';
    emailBtn.textContent = 'Use email instead';

    // Step 1: email input → "Send code".
    emailForm.className = 'play-gate-wall__email-form';
    emailForm.hidden = true;
    emailInput.type = 'email';
    emailInput.placeholder = 'you@example.com';
    emailInput.className = 'play-gate-wall__email-input';
    emailInput.autocomplete = 'email';
    emailSend.type = 'button';
    emailSend.className = 'play-gate-wall__email-send';
    emailSend.textContent = 'Send code';
    emailForm.append(emailInput, emailSend);

    // Step 2: 6-digit code input → "Verify". Revealed after send.
    codeForm.className = 'play-gate-wall__email-form';
    codeForm.hidden = true;
    codeInput.type = 'text';
    codeInput.inputMode = 'numeric';
    codeInput.autocomplete = 'one-time-code';
    codeInput.maxLength = 8;
    codeInput.placeholder = '6-digit code';
    codeInput.className = 'play-gate-wall__email-input';
    codeVerify.type = 'button';
    codeVerify.className = 'play-gate-wall__email-send';
    codeVerify.textContent = 'Verify';
    codeForm.append(codeInput, codeVerify);

    // Secondary fallback: magic link instead of a code.
    magicLinkBtn.type = 'button';
    magicLinkBtn.className = 'play-gate-wall__magiclink';
    magicLinkBtn.textContent = 'Email me a link instead';

    card.append(googleBtn, emailBtn, emailForm, codeForm, magicLinkBtn);
  }

  const status = document.createElement('p');
  status.className = 'play-gate-wall__status';
  status.setAttribute('aria-live', 'polite');
  card.append(status);

  const closeBtn = document.createElement('button');
  closeBtn.type = 'button';
  closeBtn.className = 'play-gate-wall__close';
  closeBtn.textContent = kind === 'login' ? 'Maybe later' : 'Back to cabinet';
  card.append(closeBtn);

  root.append(card);
  void title; // reserved for a future "…to play NEON GRIND" personalization
  return {
    root, googleBtn, emailBtn, emailForm, emailInput, emailSend,
    codeForm, codeInput, codeVerify, magicLinkBtn, status, closeBtn,
  };
}
